import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

function pct(n) {
  if (n == null) return '—';
  return `${n}%`;
}
function num(n) {
  if (n == null || n === 0) return <span style={{ color:'var(--ni-text-muted)' }}>0</span>;
  return n;
}

function SummaryCard({ label, value, sub, color }) {
  return (
    <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
      borderRadius:10, padding:'18px 20px', minWidth:0 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
        letterSpacing:'0.06em', marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:32, fontWeight:800, color: color || 'var(--ni-text)', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:'var(--ni-text-muted)', marginTop:6 }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize:13, fontWeight:700, color:'var(--ni-text-muted)', textTransform:'uppercase',
      letterSpacing:'0.07em', marginBottom:12, marginTop:32, paddingBottom:6,
      borderBottom:'1px solid var(--ni-border-soft)' }}>
      {children}
    </div>
  );
}

function ReasonBar({ reason, count, max, color }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
      <div style={{ width:160, fontSize:12, color:'var(--ni-text)', flexShrink:0,
        whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{reason}</div>
      <div style={{ flex:1, height:12, background:'rgba(255,255,255,0.06)', borderRadius:6, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background: color || '#60a5fa',
          borderRadius:6, transition:'width 0.3s' }} />
      </div>
      <div style={{ fontSize:12, fontWeight:700, color: color || '#60a5fa', minWidth:24,
        textAlign:'right' }}>{count}</div>
    </div>
  );
}

function DocLifecycleRow({ label, counts, color }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <tr style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
      <td style={{ padding:'9px 14px', fontSize:13, color: color, fontWeight:600 }}>{label}</td>
      <td style={{ padding:'9px 14px', fontSize:12, textAlign:'right' }}>{num(counts.Draft)}</td>
      <td style={{ padding:'9px 14px', fontSize:12, textAlign:'right' }}>{num(counts['In Review'])}</td>
      <td style={{ padding:'9px 14px', fontSize:12, textAlign:'right', color:'#46BB89', fontWeight: counts.Released > 0 ? 700 : 400 }}>{num(counts.Released)}</td>
      <td style={{ padding:'9px 14px', fontSize:12, textAlign:'right', color:'var(--ni-text-muted)' }}>{num(counts.Obsolete)}</td>
      <td style={{ padding:'9px 14px', fontSize:12, textAlign:'right', color:'var(--ni-text-muted)' }}>{total}</td>
    </tr>
  );
}

export default function QualityAnalyticsDashboard() {
  const { getToken } = useAuth();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // flowId → bool (show stations)

  useEffect(() => {
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/analytics/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => {
        if (d.ok) setData(d);
        setLoading(false);
      })
    );
  }, [getToken]);

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>Loading analytics…</div>;
  if (!data)   return <div style={{ padding:40, textAlign:'center', color:'var(--ni-text-muted)' }}>Failed to load analytics.</div>;

  const { woStats, devStats, flowStats, testStats, scrapReasons, reworkReasons, docLifecycle, notesStats } = data;
  const fpyColor = devStats.fpy == null ? 'var(--ni-text-muted)' : devStats.fpy >= 95 ? '#46BB89' : devStats.fpy >= 80 ? '#f59e0b' : '#EC2124';
  const maxRework = Math.max(...reworkReasons.map(r => r.count), 1);
  const maxScrap  = Math.max(...scrapReasons.map(r => r.count), 1);

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>QMS Analytics</h2>
        <p style={{ margin: '4px 0 0', color: 'var(--ni-text-muted)', fontSize: 13 }}>
          Live metrics from execution data across all work orders
        </p>
      </div>

      {/* ── Summary cards ──────────────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:12 }}>
        <SummaryCard label="Work Orders" value={woStats.total}
          sub={`${woStats.inProgress} in progress · ${woStats.completed} completed`} color="#60a5fa" />
        <SummaryCard label="Devices Processed" value={devStats.total}
          sub={`${devStats.passed} passed · ${devStats.scrapped} scrapped`} />
        <SummaryCard label="First Pass Yield" value={pct(devStats.fpy)}
          sub={`${devStats.firstPass} first-pass of ${devStats.passed + devStats.scrapped} completed`}
          color={fpyColor} />
        <SummaryCard label="Open Notes" value={notesStats.Open + notesStats['In Progress']}
          sub={`${notesStats.Open} open · ${notesStats['In Progress']} in progress`} color="#fb923c" />
        <SummaryCard label="Active Devices" value={devStats.active}
          sub={`${devStats.rework} awaiting rework`} color="#f59e0b" />
      </div>

      {/* ── First pass yield by flow ────────────────────────────────────────── */}
      {flowStats.length > 0 && (
        <>
          <SectionTitle>First Pass Yield by Process Flow</SectionTitle>
          <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'rgba(0,0,0,0.2)', color:'var(--ni-text-muted)', fontSize:11,
                  textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  {['Flow','Total','Passed','First Pass','Rework','Scrapped','Active','FPY'].map(h => (
                    <th key={h} style={{ padding:'9px 14px', textAlign: h==='Flow'?'left':'right', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                  <th style={{ padding:'9px 14px' }}></th>
                </tr>
              </thead>
              <tbody>
                {flowStats.map(f => (
                  <React.Fragment key={f.flowId}>
                    <tr style={{ borderTop:'1px solid var(--ni-border-soft)', cursor:'pointer' }}
                      onClick={() => setExpanded(p => ({ ...p, [f.flowId]: !p[f.flowId] }))}
                      onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ fontWeight:600 }}>{f.flowName}</div>
                        <div style={{ fontSize:11, color:'#60a5fa', fontFamily:'monospace' }}>{f.flowDocNumber}</div>
                      </td>
                      <td style={{ padding:'10px 14px', textAlign:'right' }}>{f.total}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', color:'#46BB89' }}>{num(f.passed)}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', color:'#46BB89', fontWeight:700 }}>{num(f.firstPass)}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', color:'#f59e0b' }}>{num(f.rework)}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', color:'#EC2124' }}>{num(f.scrapped)}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right', color:'var(--ni-text-muted)' }}>{num(f.active)}</td>
                      <td style={{ padding:'10px 14px', textAlign:'right' }}>
                        <span style={{ fontWeight:700, fontSize:14,
                          color: f.fpy == null ? 'var(--ni-text-muted)' : f.fpy >= 95 ? '#46BB89' : f.fpy >= 80 ? '#f59e0b' : '#EC2124' }}>
                          {pct(f.fpy)}
                        </span>
                      </td>
                      <td style={{ padding:'10px 14px', textAlign:'right', color:'var(--ni-text-muted)', fontSize:11 }}>
                        {f.stations.length > 0 ? (expanded[f.flowId] ? '▲' : '▼') : ''}
                      </td>
                    </tr>
                    {expanded[f.flowId] && f.stations.length > 0 && (
                      <tr style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                        <td colSpan={9} style={{ padding:'0 14px 12px 28px', background:'rgba(0,0,0,0.15)' }}>
                          <div style={{ fontSize:11, fontWeight:700, color:'var(--ni-text-muted)',
                            textTransform:'uppercase', letterSpacing:'0.05em', padding:'10px 0 6px' }}>
                            Station breakdown
                          </div>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                            <thead>
                              <tr style={{ color:'var(--ni-text-muted)' }}>
                                {['Station','Sign-offs','Pass','Rework/Fail','Skip','Pass Rate'].map(h => (
                                  <th key={h} style={{ padding:'4px 10px', textAlign:h==='Station'?'left':'right',
                                    fontWeight:600, fontSize:11 }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {f.stations.map(s => (
                                <tr key={s.stationId} style={{ borderTop:'1px solid rgba(255,255,255,0.05)' }}>
                                  <td style={{ padding:'5px 10px', fontWeight:600 }}>{s.stationName || s.stationId}</td>
                                  <td style={{ padding:'5px 10px', textAlign:'right' }}>{s.total}</td>
                                  <td style={{ padding:'5px 10px', textAlign:'right', color:'#46BB89' }}>{num(s.pass)}</td>
                                  <td style={{ padding:'5px 10px', textAlign:'right', color:'#f59e0b' }}>{num((s.rework||0)+(s.fail||0))}</td>
                                  <td style={{ padding:'5px 10px', textAlign:'right', color:'var(--ni-text-muted)' }}>{num(s.skip)}</td>
                                  <td style={{ padding:'5px 10px', textAlign:'right', fontWeight:700,
                                    color: s.passRate == null ? 'var(--ni-text-muted)' : s.passRate >= 95 ? '#46BB89' : s.passRate >= 80 ? '#f59e0b' : '#EC2124' }}>
                                    {pct(s.passRate)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Test pass rates ─────────────────────────────────────────────────── */}
      {testStats.length > 0 && (
        <>
          <SectionTitle>Test Pass Rates</SectionTitle>
          <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'rgba(0,0,0,0.2)', color:'var(--ni-text-muted)', fontSize:11,
                  textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  {['Test Procedure','Sessions','Passed','Failed','Pending','Pass Rate'].map(h => (
                    <th key={h} style={{ padding:'9px 14px', textAlign:h==='Test Procedure'?'left':'right', fontWeight:600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {testStats.map(t => (
                  <tr key={t.testRefId || t.testName} style={{ borderTop:'1px solid var(--ni-border-soft)' }}>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ fontWeight:600 }}>{t.testName}</div>
                      {t.testDocNumber && <div style={{ fontSize:11, color:'#34d399', fontFamily:'monospace' }}>{t.testDocNumber}</div>}
                    </td>
                    <td style={{ padding:'10px 14px', textAlign:'right' }}>{t.total}</td>
                    <td style={{ padding:'10px 14px', textAlign:'right', color:'#46BB89' }}>{num(t.passed)}</td>
                    <td style={{ padding:'10px 14px', textAlign:'right', color:'#EC2124' }}>{num(t.failed)}</td>
                    <td style={{ padding:'10px 14px', textAlign:'right', color:'var(--ni-text-muted)' }}>{num(t.pending)}</td>
                    <td style={{ padding:'10px 14px', textAlign:'right', fontWeight:700,
                      color: t.passRate == null ? 'var(--ni-text-muted)' : t.passRate >= 95 ? '#46BB89' : t.passRate >= 80 ? '#f59e0b' : '#EC2124' }}>
                      {pct(t.passRate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Defect reasons ──────────────────────────────────────────────────── */}
      {(reworkReasons.length > 0 || scrapReasons.length > 0) && (
        <>
          <SectionTitle>Defect & Scrap Reasons</SectionTitle>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {reworkReasons.length > 0 && (
              <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, padding:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#f59e0b', marginBottom:12 }}>REWORK REASONS</div>
                {reworkReasons.map(r => (
                  <ReasonBar key={r.reason} reason={r.reason} count={r.count} max={maxRework} color="#f59e0b" />
                ))}
              </div>
            )}
            {scrapReasons.length > 0 && (
              <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, padding:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#EC2124', marginBottom:12 }}>SCRAP REASONS</div>
                {scrapReasons.map(r => (
                  <ReasonBar key={r.reason} reason={r.reason} count={r.count} max={maxScrap} color="#EC2124" />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Document lifecycle ──────────────────────────────────────────────── */}
      <SectionTitle>Document Lifecycle</SectionTitle>
      <div style={{ background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)', borderRadius:10, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'rgba(0,0,0,0.2)', color:'var(--ni-text-muted)', fontSize:11,
              textTransform:'uppercase', letterSpacing:'0.06em' }}>
              <th style={{ padding:'9px 14px', textAlign:'left', fontWeight:600 }}>Domain</th>
              <th style={{ padding:'9px 14px', textAlign:'right', fontWeight:600 }}>Draft</th>
              <th style={{ padding:'9px 14px', textAlign:'right', fontWeight:600 }}>In Review</th>
              <th style={{ padding:'9px 14px', textAlign:'right', fontWeight:600, color:'#46BB89' }}>Released</th>
              <th style={{ padding:'9px 14px', textAlign:'right', fontWeight:600 }}>Obsolete</th>
              <th style={{ padding:'9px 14px', textAlign:'right', fontWeight:600 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            <DocLifecycleRow label="Quality Documents" counts={docLifecycle.documents} color="#46BB89" />
            <DocLifecycleRow label="Process Flows"     counts={docLifecycle.flows}     color="#60a5fa" />
            <DocLifecycleRow label="Jig Templates"     counts={docLifecycle.jigs}      color="#a78bfa" />
            <DocLifecycleRow label="Equipment"         counts={docLifecycle.equipment} color="#f59e0b" />
            <DocLifecycleRow label="Test Procedures"   counts={docLifecycle.tests}     color="#34d399" />
          </tbody>
        </table>
      </div>

      {flowStats.length === 0 && testStats.length === 0 && (
        <div style={{ padding:'40px 0', textAlign:'center', color:'var(--ni-text-muted)', fontSize:13 }}>
          No execution data yet. Create and run work orders to start seeing metrics here.
        </div>
      )}
    </div>
  );
}
